import React, { FC } from "react";
import { Box, makeStyles } from "@material-ui/core";
import { color, px } from "Configs/theme";
import { ExternalChatOrganization, ExternalChatUserState, } from "domain-entities";
import OrganizationSignInGcView from "./OrganizationSignInGcView";
import { useTranslation } from "Providers/TranslationProvider";
import { getImageUrl } from "Helpers/AssetHelper";
import { SaveDocumentStore } from "Helpers/StoreHelper";
import { useApp } from "Providers/AppProvider";
import environment from "Configs/environment";
import { firestore } from "firebase/app";
import { useViewer } from "Components/Auth/AuthProvider";
import { ModalController } from "../Modal/Modal";

interface Props {
  organization: ExternalChatOrganization;
  viewerUserStateStore: SaveDocumentStore<ExternalChatUserState>;
  modalController: ModalController<ExternalChatOrganization>;
}

const OrganizationSignInView: FC<Props> = (props) => {
  const viewer = useViewer();
  const {t} = useTranslation();
  const classes = useStyles();
  const {firebaseApp} = useApp();

  async function authenticate(token: string) {
    if (firebaseApp) {
      const userAuthFunction = firebaseApp
          .functions(environment.firebaseConfig.region)
          .httpsCallable("externalChatUserAuth");

      const result = await userAuthFunction({
        token,
        organizationId: props.organization.id,
      });

      if (result.data === "success") {
        await props.viewerUserStateStore.updateItem(
            {
              organizationsAdded: firestore.FieldValue.arrayUnion(
                  props.organization.id
              ) as any,
            },
            viewer.user.uid
        );
      } else {
        throw new Error(
            `Cloud function result error: ${JSON.stringify(result.data)}`
        );
      }
    } else {
      throw new Error("Cloud function error: Can not find app.");
    }
  }

  function renderCta() {
    return (
        <OrganizationSignInGcView
            organization={props.organization}
            authenticate={authenticate}
            modalController={props.modalController}
        />
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.contentOuter}>
        <Box className={classes.contentInner}>
          <Box className={classes.contentImage}>
            <img src={getImageUrl("hint.png")} alt="Hint" />
          </Box>
          <Box className={classes.contentTitle}>
            {t("OrganizationSignInView_Title")}
          </Box>
          <Box className={classes.contentMessage}>
            {t("OrganizationSignInView_Message", {
              organizationName: props.organization.name,
            })}
          </Box>
          <Box className={classes.contentCta}>{renderCta()}</Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OrganizationSignInView;

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    borderRadius: px.s,
    backgroundColor: color.card,
    padding: px.l,
    margin: px.m,
    marginLeft: px.none,
  },
  header: {
    display: "flex",
    flexDirection: "column",
    paddingBottom: px.l,
    borderBottom: `1px solid ${color.placeholder}`,
    marginBottom: px.l,
  },
  organizationName: {
    fontSize: "18px",
    fontWeight: "bold",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    marginBottom: px.xs,
  },
  organizationLocation: {
    fontSize: "14px",
    fontWeight: "bold",
    textTransform: "capitalize",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    opacity: 0.5,
  },
  contentOuter: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  contentInner: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    width: "100%",
    maxWidth: 400,
  },
  contentImage: {
    display: "flex",
    justifyContent: "center",
    marginBottom: px.xl,
    "& > img": {
      height: 180,
    },
  },
  contentTitle: {
    color: color.primary,
    fontSize: "20px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: px.m,
  },
  contentMessage: {
    fontSize: "12px",
    textAlign: "center",
    fontWeight: 500,
    marginBottom: px.xl,
  },
  contentCta: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
});
